'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function MortgageCalculator() {
  const propertyPrice = 18500000;
  const [downPaymentPct, setDownPaymentPct] = useState(25);
  const [interestRate, setInterestRate] = useState(4.5);
  const [loanTermYrs, setLoanTermYrs] = useState(25);

  const downPayment = Math.round(propertyPrice * downPaymentPct / 100);
  const loanAmount = propertyPrice - downPayment;
  const monthlyRate = interestRate / 100 / 12;
  const numPayments = loanTermYrs * 12;
  const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
  const totalRepayment = monthlyPayment * numPayments;
  const totalInterest = totalRepayment - loanAmount;

  function formatAED(n: number) {
    if (n >= 1000000) return `AED ${(n / 1000000).toFixed(2)}M`;
    return `AED ${Math.round(n).toLocaleString()}`;
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
      <div className="flex items-center gap-2 mb-5">
        <Icon name="CalculatorIcon" size={18} className="text-primary" />
        <h3 className="font-bold text-foreground text-base">Mortgage Calculator</h3>
      </div>

      {/* Property Price Display */}
      <div className="bg-muted/60 rounded-xl p-3 mb-4 text-center">
        <div className="text-xs text-muted-foreground mb-0.5">Property Price</div>
        <div className="text-xl font-extrabold text-primary font-tabular">AED 18,500,000</div>
      </div>

      {/* Down Payment Slider */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1.5">
          <label className="text-xs font-semibold text-foreground">Down Payment</label>
          <span className="text-xs font-bold text-primary">{downPaymentPct}% — {formatAED(downPayment)}</span>
        </div>
        <input
          type="range"
          min={20}
          max={80}
          step={5}
          value={downPaymentPct}
          onChange={(e) => setDownPaymentPct(Number(e.target.value))}
          className="w-full h-1.5 bg-muted rounded-full appearance-none cursor-pointer accent-primary"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-0.5">
          <span>20%</span><span>80%</span>
        </div>
      </div>

      {/* Interest Rate Slider */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1.5">
          <label className="text-xs font-semibold text-foreground">Interest Rate</label>
          <span className="text-xs font-bold text-primary">{interestRate.toFixed(1)}% p.a.</span>
        </div>
        <input
          type="range"
          min={2.5}
          max={8}
          step={0.25}
          value={interestRate}
          onChange={(e) => setInterestRate(Number(e.target.value))}
          className="w-full h-1.5 bg-muted rounded-full appearance-none cursor-pointer accent-primary"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-0.5">
          <span>2.5%</span><span>8%</span>
        </div>
      </div>

      {/* Loan Term */}
      <div className="mb-5">
        <label className="text-xs font-semibold text-foreground block mb-2">Loan Term</label>
        <div className="flex gap-1.5">
          {[10, 15, 20, 25, 30].map((yr) => (
            <button
              key={`term-${yr}`}
              onClick={() => setLoanTermYrs(yr)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                loanTermYrs === yr ? 'bg-primary text-white' : 'bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border'
              }`}
            >
              {yr}yr
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="bg-primary/8 border border-primary/20 rounded-xl p-4 space-y-2.5">
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">Loan Amount</span>
          <span className="text-sm font-bold text-foreground font-tabular">{formatAED(loanAmount)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">Monthly Payment</span>
          <span className="text-base font-extrabold text-primary font-tabular">{formatAED(monthlyPayment)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">Total Interest</span>
          <span className="text-sm font-bold text-foreground font-tabular">{formatAED(totalInterest)}</span>
        </div>
        <div className="pt-2 border-t border-primary/20 flex justify-between items-center">
          <span className="text-xs font-semibold text-foreground">Total Repayment</span>
          <span className="text-sm font-extrabold text-primary font-tabular">{formatAED(totalRepayment)}</span>
        </div>
      </div>

      <button className="w-full mt-3 py-2.5 bg-accent text-accent-foreground rounded-xl font-semibold text-sm hover:bg-accent-dark transition-all flex items-center justify-center gap-2">
        <Icon name="BanknotesIcon" size={15} />
        Request Mortgage Pre-Approval
      </button>
    </div>
  );
}